import { useState } from "react";
import { Alert, Box, Button, Dialog, Snackbar } from "@mui/material";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import ExoForm from "../../components/exo/ExoForm";
import { inviteUser } from "../../api/invitationApi";
import CreateTenantDialog from "./CreateTenantDialog";

function invitationErrorMessage(t, error) {
  const status = error?.response?.status;
  if (status === 400) {
    return t("Please enter a valid email address and a name.");
  }
  if (status === 409) {
    return t("The user already has an active account or a pending invitation.");
  }
  if (status === 502) {
    return t("The invitation could not be sent. Please try again later.");
  }
  return error?.response?.data?.message || t("Invitation failed. Please try again.");
}

const InviteUserDialog = ({ open = false, onClose, onInvited, onTenantCreated }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [noTenant, setNoTenant] = useState(false);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);

  function handleSubmit(data) {
    inviteUser({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      onLoading: setIsLoading,
      onResponse: () => {
        setMessage({ severity: "success", text: t("User invited") });
        onInvited?.();
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          setNoTenant(true);
          setMessage({
            severity: "warning",
            text: t(
              "Your user account is not assigned to an organization. Create one to invite users."
            ),
          });
        } else {
          setMessage({ severity: "error", text: invitationErrorMessage(t, error) });
        }
      },
    });
  }

  const fields = [
    {
      label: t("Email"),
      type: "textBig",
      key: "email",
    },
    {
      label: t("First name"),
      type: "textBig",
      key: "firstName",
    },
    {
      label: t("Last name"),
      type: "textBig",
      key: "lastName",
    },
  ];
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("Invalid email"))
      .required(t("Email is required")),
    firstName: Yup.string().required(t("First name is required")),
    lastName: Yup.string().required(t("Last name is required")),
  });

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Box className="p-4 flex flex-col gap-4">
          <ExoForm
            key={open ? "open" : "closed"}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            fields={fields}
            header={t("Invite user")}
            onCancle={onClose}
            isLoading={isLoading}
            submitText={t("Invite")}
          />
          {noTenant && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setTenantDialogOpen(true)}
                >
                  {t("Create organization")}
                </Button>
              }
            >
              {t(
                "Your user account is not assigned to an organization. Create one to invite users."
              )}
            </Alert>
          )}
        </Box>
      </Dialog>
      <CreateTenantDialog
        open={tenantDialogOpen}
        onClose={() => setTenantDialogOpen(false)}
        onTenantCreated={(tenant) => {
          setTenantDialogOpen(false);
          setNoTenant(false);
          setMessage({
            severity: "success",
            text: t("Organization created. You can now invite users."),
          });
          onTenantCreated?.(tenant);
        }}
      />
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={7000}
        onClose={(event, reason) => {
          if (reason === "clickaway") {
            return;
          }
          setMessage(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {message && (
          <Alert
            severity={message.severity}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}
      </Snackbar>
    </>
  );
};

export default InviteUserDialog;

export { invitationErrorMessage };
